import React, { useEffect, useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, Scale, TrendingUp } from "lucide-react";
import { api, money, dateFmt } from "../lib/api.js";
import { Modal, Notice } from "./UI.jsx";
const empty = () => ({
  accountId: "",
  operationDate: new Date().toISOString().slice(0, 10),
  amount: "",
  direction: "expense",
  financialType: "expense",
  description: "",
  counterparty: "",
  reference: "",
  comments: "",
  categoryId: "",
  costCenterId: "",
  projectId: "",
  budgetId: "",
  budgetBucket: "unclassified",
  clearanceStatus: "not_cleared",
  incomeQuadrantOverride: "",
  learnQuadrantFromCategory: false,
  assetId: "",
  assetRole: "purchase",
  assetQuantity: "",
  assetUnitPrice: "",
  assetService: "",
  fromAccountId: "",
  toAccountId: "",
  receivedAmount: "",
  tags: [],
});
export default function MovementModal({
  open,
  onClose,
  onSaved,
  initial = null,
  context = {},
}) {
  const [cfg, setCfg] = useState(null),
    [f, setF] = useState(empty),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [matches, setMatches] = useState([]),
    [matchKey, setMatchKey] = useState(""),
    [placeId, setPlaceId] = useState(""),
    [tagSearch,setTagSearch]=useState(""),
    [newTag,setNewTag]=useState(""),
    [step, setStep] = useState(1);
  useEffect(() => {
    if (!open) return;
    setStep(context.accountId ? 2 : 1);
    setError("");
    Promise.all([api("/config"), api("/accounts")])
      .then(([c, accounts]) => {
        c.accounts = accounts;
        setCfg(c);
        const account =
            accounts.find(
              (a) =>
                String(a.id) ===
                String(context.accountId || initial?.account_id),
            ) || accounts[0],
          amount = Number(initial?.amount || 0),
          linkedTransfer = initial?.financial_type === "transfer",
          fromId = linkedTransfer
            ? amount < 0
              ? initial.account_id
              : initial.transfer_account_id
            : account?.id,
          toId = linkedTransfer
            ? amount < 0
              ? initial.transfer_account_id
              : initial.account_id
            : "";
        setPlaceId(String(account?.location_id || account?.bank_id || "other"));
        setF({
          ...empty(),
          accountId: String(account?.id || ""),
          fromAccountId: String(fromId || ""),
          toAccountId: String(toId || ""),
          receivedAmount: initial ? String(Math.abs(amount)) : "",
          operationDate:
            String(initial?.operation_date || "").slice(0, 10) ||
            new Date().toISOString().slice(0, 10),
          amount: initial ? String(Math.abs(amount)) : String(context.amount || ""),
          direction: !initial ? (context.direction || "expense") :
            initial?.reference === "pisto:balance_adjustment"
              ? "balance"
              : initial?.financial_type === "transfer"
              ? (initial?.asset_id && ["purchase","sale"].includes(initial?.asset_role) ? "investment" : "transfer")
              : amount > 0
                ? "income"
                : "expense",
          financialType: initial?.financial_type || ((context.direction || (amount > 0 ? "income" : "expense")) === "income" ? "active_income" : "expense"),
          description: initial?.description || context.description || "",
          counterparty: initial?.counterparty || "",
          reference: initial?.reference || "",
          comments: initial?.comments || "",
          categoryId: String(initial?.category_id || ""),
          costCenterId: String(
            initial?.cost_center_id || account?.cost_center_id || "",
          ),
          projectId: String(initial?.project_id || ""),
          budgetId: String(initial?.budget_id || ""),
          budgetBucket: initial?.budget_bucket || "unclassified",
          clearanceStatus: initial?.clearance_status || "not_cleared",
          incomeQuadrantOverride: initial?.income_quadrant_override || "",
          learnQuadrantFromCategory: false,
          assetId: String(initial?.asset_id || context.assetId || ""),
          assetRole: initial?.asset_role || context.assetRole || "",
          assetQuantity: initial?.asset_quantity || "",
          assetUnitPrice: initial?.asset_unit_price || "",
          assetService: (()=>{try{return JSON.parse(initial?.asset_details_json||"{}").service||""}catch{return ""}})(),
          tags: Array.isArray(initial?.tags)?initial.tags:[],
        });
      })
      .catch((e) => setError(e.message));
  }, [open, initial, context?.accountId]);
  useEffect(() => {
    if (
      !open ||
      f.direction !== "transfer" ||
      !initial ||
      initial.transfer_group_id
    ) {
      setMatches([]);
      setMatchKey("");
      return;
    }
    api(`/transactions/${initial.id}/matches?accountId=${initial.account_id}`)
      .then(setMatches)
      .catch(() => setMatches([]));
  }, [open, f.direction, initial]);
  if (!open) return null;
  const stages = initial
    ? ["all"]
    : f.direction === "expense"
      ? ["account", "basic", "budget", "classify", "details"]
      : f.direction === "income"
        ? ["account", "basic", "classify", "details"]
        : f.direction === "investment"
          ? ["account", "basic", "classify", "details"]
        : f.direction === "transfer"
          ? ["account", "basic", "details"]
          : ["account", "basic"];
  const stage = stages[Math.min(step - 1, stages.length - 1)];
  const show = (name) => Boolean(initial) || stage === name;
  const changeDirection = (direction) => {
    setStep(2);
    setF({ ...f, direction, financialType:direction==="income"?"active_income":direction==="expense"?"expense":direction==="balance"?"excluded":direction==="investment"?"transfer":f.financialType, assetRole:direction==="investment"?"purchase":f.assetRole, assetId:direction==="balance"?"":f.assetId, budgetId:direction==="balance"?"":f.budgetId });
  };
  const restartWizard = () => {
    const account = cfg.accounts.find((item) => String(item.id) === String(context.accountId || f.accountId)) || cfg.accounts[0];
    setF({
      ...empty(),
      accountId: String(account?.id || ""),
      fromAccountId: String(account?.id || ""),
      costCenterId: String(account?.cost_center_id || ""),
      assetId: String(context.assetId || ""),
    });
    setStep(1);
    setError("");
  };
  const nextStep = () => {
    setError("");
    if (stage === "account" && !f.accountId) return setError("Choose an account.");
    if (stage === "basic") {
      if (!Number.isFinite(Number(f.amount)) || Number(f.amount) <= 0) return setError("Enter an amount.");
      if (["transfer","investment"].includes(f.direction) && (!f.fromAccountId || !f.toAccountId)) return setError("Choose both accounts.");
    }
    if (stage === "classify" && f.direction === "investment" && !f.assetId) return setError("Choose the stock, fund, bond or other holding.");
    setStep((value) => Math.min(stages.length, value + 1));
  };
  const setAsset = (id) => {
    const asset = cfg.assets.find((x) => String(x.id) === id);
    if (!asset) return setF({ ...f, assetId: "" });
    const occupied = f.projectId || f.costCenterId || f.budgetId,
      apply =
        !occupied ||
        confirm(
          "Use this asset’s project, cost center and budget defaults? Choose Cancel to keep the movement values.",
        );
    const hint=`${f.description} ${cfg.categories.find(c=>String(c.id)===String(f.categoryId))?.custom_name||""}`.toLowerCase(),inferredRole=/dividend|dividendo/.test(hint)?"dividend":/interest|coupon|inter[eé]s/.test(hint)?"interest":/commission|fee|comisi[oó]n/.test(hint)?"commission":/sell|sale|venta/.test(hint)?"sale":/buy|purchase|compra/.test(hint)?"purchase":"";
    setF({
      ...f,
      assetId: id,
      assetRole:f.assetRole||inferredRole,
      ...(apply
        ? {
            projectId: String(asset.project_id || f.projectId),
            costCenterId: String(asset.cost_center_id || f.costCenterId),
            budgetId: String(asset.budget_id || f.budgetId),
          }
        : {}),
    });
  };
  const createTag=async()=>{const name=newTag.trim();if(!name)return;try{const result=await api('/tags',{method:'POST',body:{name}});const finalName=result?.name||name;setCfg({...cfg,tags:[...cfg.tags,{id:result.id||`new-${Date.now()}`,name:finalName,is_active:1}]});setF({...f,tags:[...new Set([...f.tags,finalName])]});setNewTag('');setTagSearch('')}catch(e){if(e?.suggestion){const use=confirm(`${e.message}\n\nUse “${e.suggestion.name}” instead?`);if(use){setF({...f,tags:[...new Set([...f.tags,e.suggestion.name])]});setNewTag('');}}else setError(e.message)}};
  const save = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      const enteredAmount = Number(f.amount);
      const selectedAccount = cfg.accounts.find((account)=>String(account.id)===String(f.accountId));
      const balanceDifference = f.direction === "balance" ? enteredAmount - Number(selectedAccount?.current_balance || 0) : null;
      const amount = f.direction === "balance" ? Math.abs(balanceDifference) : Math.abs(enteredAmount);
      if (!Number.isFinite(enteredAmount) || (f.direction !== "balance" && !amount)) throw new Error("Enter an amount.");
      if (f.direction === "balance" && Math.abs(balanceDifference) < 0.0001) throw new Error("The account already has this balance.");
      if (f.clearanceStatus === "planned") {
        if (f.operationDate <= new Date().toISOString().slice(0, 10))
          throw new Error("A planned movement needs a future date.");
        if (!f.budgetId)
          throw new Error("A planned movement must be linked to a budget.");
      }
      if (["transfer","investment"].includes(f.direction)) {
        if (initial && !initial.transfer_group_id && matchKey) {
          const match = matches.find(
            (x) => `${x.accountId}:${x.id}` === matchKey,
          );
          if (!match) throw new Error("Choose a valid matching movement.");
          await api("/transfers/link", {
            method: "POST",
            body: {
              firstAccountId: initial.account_id,
              firstId: initial.id,
              secondAccountId: match.accountId,
              secondId: match.id,
            },
          });
        } else {
          const transferResult = await api(
            initial?.transfer_group_id
              ? `/transfers/${initial.transfer_group_id}`
              : "/transfers",
            {
              method: initial?.transfer_group_id ? "PUT" : "POST",
              body: {
                fromAccountId: Number(f.fromAccountId),
                toAccountId: Number(f.toAccountId),
                sentAmount: amount,
                receivedAmount: Number(f.receivedAmount || amount),
                operationDate: f.operationDate,
                description: f.description,
                clearanceStatus: f.clearanceStatus,
              },
            },
          );
          if (f.direction === "investment") {
            const outgoingId = initial?.id || transferResult?.ids?.[0];
            if (!outgoingId) throw new Error("The linked transfer was saved, but the investment holding could not be linked.");
            await api(`/transactions/${outgoingId}/asset`, { method:"PATCH", body:{accountId:Number(f.fromAccountId),assetId:Number(f.assetId),assetRole:f.assetRole||"purchase",assetQuantity:f.assetQuantity?Number(f.assetQuantity):null,assetUnitPrice:f.assetUnitPrice?Number(f.assetUnitPrice):null,assetDetails:{investmentTransaction:true},projectId:f.projectId||null,costCenterId:f.costCenterId||null,budgetId:f.budgetId||null} });
          }
        }
      } else {
        const body = {
          ...f,
          accountId: Number(f.accountId),
          amount: f.direction === "balance" ? balanceDifference : f.direction === "income" ? amount : -amount,
          financialType: f.direction === "balance" ? "excluded" : f.financialType || (f.direction === "income" ? "active_income" : "expense"),
          description: f.direction === "balance" ? (f.description || "Balance adjustment") : f.description,
          reference: f.direction === "balance" ? "pisto:balance_adjustment" : f.reference,
          categoryId: f.categoryId || null,
          costCenterId: f.costCenterId || null,
          projectId: f.projectId || null,
        };
        const r = initial
          ? await api(`/transactions/${initial.id}`, { method: "PUT", body })
          : await api("/transactions", { method: "POST", body });
        const id = initial?.id || r.id;
        await api(`/transactions/${id}/asset`, {
          method: "PATCH",
          body: {
            accountId: Number(f.accountId),
            assetId: f.assetId ? Number(f.assetId) : null,
            assetRole: f.assetRole,
            assetQuantity: f.assetQuantity ? Number(f.assetQuantity) : null,
            assetUnitPrice: f.assetUnitPrice ? Number(f.assetUnitPrice) : null,
            assetDetails: f.assetService ? { service: f.assetService } : {},
            projectId: f.projectId || null,
            costCenterId: f.costCenterId || null,
            budgetId: f.budgetId || null,
          },
        });
      }
      window.dispatchEvent(new Event("pisto:data-changed"));
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={initial ? `${dateFmt(initial.operation_date)} · ${String(initial.description||"Movement").slice(0,70)}${String(initial.description||"").length>70?"…":""} · ${money(initial.amount,initial.currency)}` : `Add movement · ${step} of ${stages.length}`}
      onClose={onClose}
      screenId={`D0201-${step}`}
    >
      {!cfg ? (
        <div className="loading">Loading…</div>
      ) : (
        <form onSubmit={save} className="form-grid movement-form">
          {!initial && <div className="wizard-draft-toolbar movement-draft-toolbar span2"><span>Your unfinished movement is kept while this page remains open.</span><button type="button" className="text-button" onClick={restartWizard}>Restart / clear</button></div>}
          {show("account") && <div className="span2 movement-choice-step">
            <h2>Which account is this movement for?</h2>
            <p className="muted">Choose the place first, then select one of its accounts.</p>
            <label>Place<select value={placeId} onChange={e=>setPlaceId(e.target.value)}>{[...new Map(cfg.accounts.filter(a=>a.is_active!==0).map(a=>[String(a.location_id||a.bank_id||"other"),a.location?.name||a.location_name||a.bank_name||"Other accounts"])).entries()].map(([key,name])=><option key={key} value={key}>{name}</option>)}</select></label>
            <div className="wizard-choice-grid">
              {cfg.accounts.filter(account=>account.is_active!==0&&String(account.location_id||account.bank_id||"other")===placeId).map(account=><button type="button" className={`choice-card ${String(f.accountId)===String(account.id)?"active":""}`} key={account.id} onClick={()=>{setF({...f,accountId:String(account.id),fromAccountId:String(account.id),costCenterId:String(account.cost_center_id||"")});setTimeout(()=>setStep(2),400)}}><b>{account.name}</b><small>{account.account_type?.replaceAll("_"," ")} · {account.currency}</small></button>)}
            </div>
          </div>}
          {!show("account") && <div className="context-banner span2">
            Account:{" "}
            <b>
              {
                cfg.accounts.find((a) => String(a.id) === String(f.accountId))
                  ?.name
              }
            </b>
          </div>}
          {show("basic") && <label className="span2 amount-first">
            {f.direction === "balance" ? "Current account balance" : "Amount"}
            <div className="amount-entry"><input
              autoFocus
              type="number"
              step="0.01"
              required
              value={f.amount}
              onChange={(e) => setF({ ...f, amount: e.target.value })}
            /><span>{cfg.accounts.find((a)=>String(a.id)===String(f.accountId))?.currency||""}</span></div>
          </label>}
          {show("basic") && <div className="span2 direction-switch five">
            {[
              ["income", "INCOME", ArrowDownToLine],
              ["expense", "EXPENSE / OUT", ArrowUpFromLine],
              ["transfer", "TRANSFER", ArrowLeftRight],
              ["balance", "BALANCE", Scale],
            ].map(([v, n, I]) => (
              <button
                type="button"
                key={v}
                className={`direction ${v} ${f.direction === v ? "selected" : ""}`}
                disabled={Boolean(initial?.transfer_group_id) && !["transfer","investment"].includes(v)}
                title={
                  initial?.transfer_group_id && v !== "transfer"
                    ? "A linked transfer must remain a transfer so both sides stay in sync."
                    : undefined
                }
                onClick={() => changeDirection(v)}
              >
                <I />
                {n}
              </button>
            ))}
          </div>}
          {["transfer","investment"].includes(f.direction) && show("basic") ? (
            <>
              {initial && !initial.transfer_group_id && matches.length > 0 && (
                <label className="span2">
                  Use an existing receiving movement first
                  <select
                    value={matchKey}
                    onChange={(e) => setMatchKey(e.target.value)}
                  >
                    <option value="">
                      Create a missing counterpart instead
                    </option>
                    {matches.map((x) => (
                      <option
                        key={`${x.accountId}:${x.id}`}
                        value={`${x.accountId}:${x.id}`}
                      >
                        {x.score}% · {x.accountName} · {x.date} · {x.amount}{" "}
                        {x.currency}
                      </option>
                    ))}
                  </select>
                  <small>
                    Linking an existing match prevents a third movement from
                    being created.
                  </small>
                </label>
              )}
              <label>
                {f.direction==="investment"?"Pay from":"From account"}
                <select
                  value={f.fromAccountId}
                  onChange={(e) =>
                    setF({ ...f, fromAccountId: e.target.value })
                  }
                >
                  {[...new Map(cfg.accounts.map(a=>[a.location?.name||a.location_name||a.bank_name||"Other accounts",[]])).keys()].map(place=><optgroup key={place} label={place}>{cfg.accounts.filter(a=>(a.location?.name||a.location_name||a.bank_name||"Other accounts")===place).map(a=><option key={a.id} value={a.id}>{a.name} · {a.currency}</option>)}</optgroup>)}
                </select>
              </label>
              <label>
                {f.direction==="investment"?"Investment / brokerage account":"To account"}
                <select
                  value={f.toAccountId}
                  onChange={(e) => setF({ ...f, toAccountId: e.target.value })}
                >
                  <option value="">Choose</option>
                  {[...new Map(cfg.accounts.filter(a=>String(a.id)!==String(f.fromAccountId)).map(a=>[a.location?.name||a.location_name||a.bank_name||"Other accounts",[]])).keys()].map(place=><optgroup key={place} label={place}>{cfg.accounts.filter(a=>String(a.id)!==String(f.fromAccountId)&&(a.location?.name||a.location_name||a.bank_name||"Other accounts")===place).map(a=><option key={a.id} value={a.id}>{a.name} · {a.currency}</option>)}</optgroup>)}
                </select>
              </label>
              <label>
                Date
                <input
                  type="date"
                  required
                  value={f.operationDate}
                  onChange={(e) =>
                    setF({ ...f, operationDate: e.target.value })
                  }
                />
              </label>
              <label>
                Received amount
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={f.receivedAmount}
                  onChange={(e) =>
                    setF({ ...f, receivedAmount: e.target.value })
                  }
                />
              </label>
            </>
          ) : f.direction === "investment" && show("classify") ? <>
            <div className="span2"><h2>What investment did you buy or sell?</h2><p className="muted">Pisto records a linked transfer of value, not an expense. Only commissions and taxes reduce net worth.</p></div>
            <label>Holding<select required value={f.assetId} onChange={e=>setAsset(e.target.value)}><option value="">Choose holding</option>{cfg.assets.filter(a=>["stock","etf","fund","bond","gold","precious_metal","business"].includes(a.asset_type)).map(a=><option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
            <label>Transaction<select value={f.assetRole} onChange={e=>setF({...f,assetRole:e.target.value})}><option value="purchase">Buy</option><option value="sale">Sell</option></select></label>
            <label>Quantity<input type="number" min="0" step="any" value={f.assetQuantity} onChange={e=>setF({...f,assetQuantity:e.target.value})}/></label>
            <label>Price per unit<input type="number" min="0" step="any" value={f.assetUnitPrice} onChange={e=>setF({...f,assetUnitPrice:e.target.value})}/></label>
          </> : !["transfer","investment"].includes(f.direction) ? (
            <>
              {show("basic") && <label>
                Date
                <input
                  type="date"
                  required
                  value={f.operationDate}
                  onChange={(e) =>
                    setF({ ...f, operationDate: e.target.value })
                  }
                />
              </label>}
              {show("basic") && <label>
                <span title="Not cleared means not yet checked against the bank. Cleared means confirmed at the bank. Reconciled means included in a completed balance check.">Status ⓘ</span>
                <select
                  value={f.clearanceStatus}
                  onChange={(e) =>
                    setF({ ...f, clearanceStatus: e.target.value })
                  }
                >
                  <option value="planned">Planned</option>
                  <option value="not_cleared">Not cleared</option>
                  <option value="cleared">Cleared</option>
                  <option value="reconciled">Reconciled</option>
                </select>
              </label>}
              {show("basic") && <label className="span2">
                Description
                <input
                  className={f.description ? "prefilled" : ""}
                  value={f.description}
                  onChange={(e) => setF({ ...f, description: e.target.value })}
                />
              </label>}
              {show("classify") && f.direction !== "balance" && <label>
                Category
                <select
                  value={f.categoryId}
                  onChange={(e) => setF({ ...f, categoryId: e.target.value })}
                >
                  <option value="">Uncategorized</option>
                  {cfg.categories
                    .filter((x) => x.is_active !== 0)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {"—".repeat(Math.max(0, x.level - 1))} {x.custom_name}
                      </option>
                    ))}
                </select>
              </label>}
              {show("classify") && f.direction !== "balance" && <label>Financial classification<select value={f.financialType} onChange={e=>setF({...f,financialType:e.target.value})}>{f.direction==="income"&&<><option value="active_income">Active income</option><option value="passive_income">Passive income</option></>} {f.direction==="expense"&&<><option value="expense">Expense</option><option value="investment">Investment contribution</option><option value="excluded">Excluded from reports</option></>}</select></label>}
              {show("classify") && f.direction === "income" && <>
                <label>Income source exception<select value={f.incomeQuadrantOverride} onChange={e=>setF({...f,incomeQuadrantOverride:e.target.value,learnQuadrantFromCategory:e.target.value&&e.target.value!=="none"?f.learnQuadrantFromCategory:false})}><option value="">Use category default</option><option value="employee">Employee</option><option value="self_employed">Self-employed</option><option value="business_owner">Business owner</option><option value="investor">Investor</option><option value="unclassified">Review later</option><option value="none">None of these / not income</option></select><small>This identifies where realised income came from. Asset purchases, sales and balances are excluded.</small></label>
                {f.incomeQuadrantOverride && f.incomeQuadrantOverride!=="none" && f.categoryId && <label className="check"><input type="checkbox" checked={f.learnQuadrantFromCategory} onChange={e=>setF({...f,learnQuadrantFromCategory:e.target.checked})}/> Use this as the default for this category</label>}
              </>}
              {show("budget") && f.direction === "expense" && (
                <div className="span2 movement-choice-step">
                  <h2>How does this expense fit your priorities?</h2>
                  <p className="muted">Choose the closest answer. You can change it later.</p>
                  <div className="wizard-choice-grid">
                    {[["needs","I need it","Essential living costs"],["wants","I want it","Optional enjoyment or lifestyle"],["goals","It is for a goal / saving","Saving, investing or paying down debt"],["unclassified","I don’t know","Leave it for quick review"]].map(([value,label,help])=><button type="button" key={value} className={`choice-card ${f.budgetBucket===value?"active":""}`} onClick={()=>{setF({...f,budgetBucket:value});setTimeout(()=>setStep(stages.indexOf("classify")+1),450)}}><b>{label}</b><small>{help}</small></button>)}
                  </div>
                </div>
              )}
              {show("classify") && f.direction !== "balance" && <label>
                Link asset / investment
                <select
                  value={f.assetId}
                  onChange={(e) => setAsset(e.target.value)}
                >
                  <option value="">None</option>
                  {cfg.assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </label>}
              {show("classify") && f.assetId && (
                <label>
                  Asset activity
                  <select
                    value={f.assetRole}
                    onChange={(e) => setF({ ...f, assetRole: e.target.value })}
                  >
                    <option value="">Choose how this relates</option>{(() => { const type=cfg.assets.find(a=>String(a.id)===String(f.assetId))?.asset_type;return type==="real_estate"?["purchase","rental income","rental payment","renovation","maintenance","association/shared-area fees","property tax","insurance","utilities/services","agent fee","notary/legal cost","sale","other"]:type==="vehicle"?["purchase","loan payment","insurance","registration/tax","fuel","maintenance","repair","parking/tolls","rental income","sale","other"]:["stock","etf","fund"].includes(type)?["purchase","sale","dividend","tax","commission","custody fee","stock split","merger","other"]:type==="loan_liability"?["loan received","principal repayment","interest","fee","other"]:["bond","loan_receivable"].includes(type)?["principal advanced","principal repayment","interest","tax","commission","default/write-off","other"]:["purchase","sale","income","maintenance","tax","commission","other"] })().map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </select><small>How does this movement relate to the selected asset? Purchases and sales change quantity and cost basis; income, fees and maintenance are related activity only.</small>
                </label>
              )}
              {show("classify") && f.assetId && ["purchase","sale"].includes(f.assetRole) && <><label>Quantity bought or sold<input type="number" step="any" value={f.assetQuantity} onChange={e=>setF({...f,assetQuantity:e.target.value})}/></label><label>Price per unit<input type="number" step="any" value={f.assetUnitPrice} onChange={e=>setF({...f,assetUnitPrice:e.target.value})}/><small>The movement amount remains the authoritative cash value.</small></label></>}
              {show("classify") && f.assetId && f.assetRole==="utilities/services" && <label>Service<select value={f.assetService} onChange={e=>setF({...f,assetService:e.target.value})}><option value="">Choose service</option>{["electricity","water","gas","internet","security","cleaning","waste","other"].map(x=><option key={x}>{x}</option>)}</select></label>}
              {show("details") && <><div className="span2"><h2>Would you like to organize it further?</h2><p className="muted">These details are optional unless this is a planned movement.</p></div><label>
                Budget
                <select
                  value={f.budgetId}
                  onChange={(e) => setF({ ...f, budgetId: e.target.value })}
                >
                  <option value="">No budget</option>
                  {cfg.budgets.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Project
                <select
                  value={f.projectId}
                  onChange={(e) => setF({ ...f, projectId: e.target.value })}
                >
                  <option value="">No project</option>
                  {cfg.projects.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Cost center
                <select
                  value={f.costCenterId}
                  onChange={(e) => setF({ ...f, costCenterId: e.target.value })}
                >
                  <option value="">None</option>
                  {cfg.costCenters.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Counterparty / merchant
                <input
                  value={f.counterparty}
                  onChange={(e) => setF({ ...f, counterparty: e.target.value })}
                />
              </label>
              <label>
                Reference / bank details
                <input
                  value={f.reference}
                  onChange={(e) => setF({ ...f, reference: e.target.value })}
                />
              </label>
              <label className="span2">
                Tags
                <input value={tagSearch} onChange={e=>setTagSearch(e.target.value)} placeholder="Search tags"/>
                <div className="tag-picker">
                  {cfg.tags
                    .filter((x) => x.is_active !== 0&&x.name.toLowerCase().includes(tagSearch.toLowerCase()))
                    .map((x) => (
                      <button
                        type="button"
                        className={
                          f.tags.includes(x.name)
                            ? "quick-pill active"
                            : "quick-pill"
                        }
                        key={x.id}
                        onClick={() =>
                          setF({
                            ...f,
                            tags: f.tags.includes(x.name)
                              ? f.tags.filter((t) => t !== x.name)
                              : [...f.tags, x.name],
                          })
                        }
                      >
                        {x.name}
                      </button>
                    ))}
                </div>
                <div className="inline-create"><input value={newTag} onChange={e=>setNewTag(e.target.value)} placeholder="New tag" onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();createTag()}}}/><button type="button" className="secondary" onClick={createTag}>+ Add tag</button></div>
              </label></>}
              {show("details")&&<label className="span2">Private comment<textarea value={f.comments} onChange={e=>setF({...f,comments:e.target.value})} rows="3" placeholder="Optional note about this movement"/><small>Encrypted with your personal data key.</small></label>}
            </>
          ) : show("details") ? <>
            <div className="span2"><h2>{f.direction==="investment"?"Review investment transaction":"Review transfer"}</h2><p className="muted">{f.direction==="investment"?"The linked value transfer is excluded from income and expense reports.":"Add an optional description, then save both linked movements."}</p></div>
            <label className="span2">Description<input value={f.description} onChange={e=>setF({...f,description:e.target.value})} placeholder="What was this transfer for?" /></label>
            <label><span title="Not cleared means not yet checked against the bank. Cleared means confirmed at the bank.">Status ⓘ</span><select value={f.clearanceStatus} onChange={e=>setF({...f,clearanceStatus:e.target.value})}><option value="not_cleared">Not cleared</option><option value="cleared">Cleared</option><option value="reconciled">Reconciled</option></select></label>
            <div className="review-summary"><b>{f.amount} {cfg.accounts.find(a=>String(a.id)===String(f.fromAccountId))?.currency}</b><small>{cfg.accounts.find(a=>String(a.id)===String(f.fromAccountId))?.name} → {cfg.accounts.find(a=>String(a.id)===String(f.toAccountId))?.name}</small></div>
          </> : null}
          {error && (
            <Notice tone="error" className="span2">
              {error}
            </Notice>
          )}
          {!initial && <div className="span2 asset-wizard-nav">
            <button type="button" className="wizard-nav-button" disabled={step===1} onClick={()=>{setError("");setStep(value=>Math.max(1,value-1))}}>← Back</button>
            {step < stages.length && <button type="button" className="wizard-nav-button" onClick={nextStep}>Next →</button>}
          </div>}
          {(initial || step === stages.length) && <button className="primary span2 modal-submit" disabled={busy}>
            {busy ? "Saving…" : "Save movement"}
          </button>}
        </form>
      )}
    </Modal>
  );
}
